import React, { useContext } from "react";
import Head from "next/head";
import { bool, node, string } from "prop-types";
import AssetsProvider from "@insightfulscience/atomic-react/utils/AssetsProvider";

const Robots = ({ content = "" }) => (
	<Head>
		<meta name="robots" content={content} />
	</Head>
);

Robots.propTypes = {
	content: string,
};

const Author = ({ content = "" }) => (
	<Head>
		<meta name="author" content={content} />
	</Head>
);

Author.propTypes = {
	content: string,
};

const Canonical = ({ href }) => (
	<Head>
		<link rel="canonical" href={href} />
	</Head>
);

Canonical.propTypes = {
	href: string.isRequired,
};

const CanonicalAlternate = ({ type = "text/html", hrefLang, href, title = "" }) => (
	<Head>
		<link rel="alternate" type={type} hrefLang={hrefLang} href={href} title={title} />
	</Head>
);

CanonicalAlternate.propTypes = {
	type: string,
	hrefLang: string.isRequired,
	href: string.isRequired,
	title: string,
};

export const MetaTwitter = ({
	card = "",
	title = "",
	description = "",
	site = "",
	image = "",
	creator = "",
	isImageBaseUrl = false,
}) => {
	const baseUrl = isImageBaseUrl ? useContext(AssetsProvider.context) : "";
	return (
		<Head>
			{title && <meta name="twitter:title" content={title} />}
			{description && <meta name="twitter:description" content={description} />}
			{card && <meta name="twitter:card" content={card} />}
			{site && <meta name="twitter:site" content={site} />}
			{image && <meta name="twitter:image" content={`${baseUrl}${image}`} />}
			{creator && <meta name="twitter:creator" content={creator} />}
		</Head>
	);
};

MetaTwitter.propTypes = {
	card: string,
	title: string,
	description: string,
	site: string,
	image: string,
	creator: string,
	isImageBaseUrl: bool,
};

export const MetaOGP = ({
	isImageBaseUrl = false,
	title = "",
	description = "",
	locale = "",
	type = "",
	url = "",
	image = "",
	imageSecureUrl = "",
	siteName = "",
	articlePublisher = "",
}) => {
	const baseUrl = isImageBaseUrl ? useContext(AssetsProvider.context) : "";
	return (
		<Head>
			{title && <meta property="og:title" content={title} />}
			{description && <meta property="og:description" content={description} />}
			{locale && <meta property="og:locale" content={locale} />}
			{type && <meta property="og:type" content={type} />}
			{url && <meta property="og:url" content={url} />}
			{image && <meta property="og:image" content={`${baseUrl}${image}`} />}
			{imageSecureUrl && (
				<meta property="og:image:secure_url" content={`${baseUrl}${imageSecureUrl}`} />
			)}
			{siteName && <meta property="og:site_name" content={siteName} />}
			{articlePublisher && <meta property="article:publisher" content={articlePublisher} />}
		</Head>
	);
};

MetaOGP.propTypes = {
	isImageBaseUrl: bool,
	title: string,
	description: string,
	locale: string,
	type: string,
	url: string,
	image: string,
	imageSecureUrl: string,
	siteName: string,
	articlePublisher: string,
};

const PageMeta = ({ title = "", description = "", children = "" }) => {
	return (
		<>
			<Head>
				{title && <title>{title}</title>}
				{description && <meta name="description" content={description} />}
			</Head>
			{children}
		</>
	);
};

const CustomHead = ({ children }) => {
	return <Head>{children}</Head>;
};

CustomHead.propTypes = {
	children: node,
};

PageMeta.Robots = Robots;
PageMeta.Custom = CustomHead;
PageMeta.Canonical = Canonical;
PageMeta.CanonicalAlternate = CanonicalAlternate;
PageMeta.Author = Author;
PageMeta.Twitter = MetaTwitter;
PageMeta.OGP = MetaOGP;

PageMeta.propTypes = {
	title: string,
	description: string,
	children: node,
};

export default PageMeta;
